/**
 * BlockEditor.tsx — coresignal-lab-style block editor for the talent-pool query.
 *
 * Semantic cards (Work Experience / Education / Skills / Languages / person-field / advanced group) with a
 * WHEN scope where relevant and ADD-on-demand sub-field rows, under a top "Match ALL / ANY · not?" header.
 * It edits the block VIEW; every change recompiles to our `Group/Filter` tree via blocksToTree and flows
 * up through onChange, so /count, the rail and saved searches keep reading the same AST.
 *
 * Visual language mirrors coresignal styles.css: clean white cards, coral (#FD775E) as the active/selected
 * accent, navy (#0D1830) for the structural Match toggle, 84px-label sub-field rows, dashed +Field chips.
 */

import { useState } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import type { Group } from '@/lib/talentPool.api';
import { AutocompleteInput, Similarity, FilterSelect } from '../controls';
import { LeafValueEditor, newLeaf, EXPERIENCE_WHEN } from '../leafFields';
import { PROFICIENCY_OPTS } from '../tpEnums';
import { TreeFilters } from '../TreeFilters';
import {
    type Block,
    type WhenState,
    treeToBlocks,
    blocksToTree,
    fieldLabel,
    EXPERIENCE_ROW_FIELDS,
    EDUCATION_ROW_FIELDS,
    PERSON_FIELD_BLOCKS,
    newExperienceBlock,
    newEducationBlock,
    newSkillsBlock,
    newLanguagesBlock,
    newFieldBlock,
} from './blockModel';

type Opt = { value: string; label: string };

// ── shared atoms ──
function Seg({ value, options, onChange, tone = 'navy' }: { value: string; options: Opt[]; onChange: (v: string) => void; tone?: 'navy' | 'coral' }) {
    const active = tone === 'coral' ? 'bg-[#FD775E] text-white' : 'bg-[#0D1830] text-white';
    return (
        <div className="inline-flex rounded-lg bg-zinc-100 p-0.5 text-[12px]">
            {options.map((o) => (
                <button
                    key={o.value}
                    type="button"
                    onClick={() => onChange(o.value)}
                    className={`rounded-md px-2.5 py-1 font-medium transition-colors ${value === o.value ? active : 'text-zinc-600 hover:text-zinc-800'}`}
                >
                    {o.label}
                </button>
            ))}
        </div>
    );
}

function ExcludeBtn({ on, onChange }: { on: boolean; onChange: (v: boolean) => void }) {
    return (
        <button
            type="button"
            onClick={() => onChange(!on)}
            title="Exclude candidates matching this"
            className={`rounded-md border px-2 py-0.5 text-[11px] transition-colors ${on ? 'border-[#FD775E] bg-[#FFF1ED] font-medium text-[#B23A22]' : 'border-zinc-200 bg-white text-zinc-500 hover:text-[#FD775E]'}`}
        >
            {on ? 'NOT' : 'exclude'}
        </button>
    );
}

function BlockCard({ title, controls, onRemove, children }: { title: string; controls?: React.ReactNode; onRemove?: () => void; children: React.ReactNode }) {
    return (
        <div className="mb-3 rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm">
            <div className="mb-2 flex flex-wrap items-center gap-2">
                <span className="text-[13.5px] font-semibold tracking-tight text-[#0D1830]">{title}</span>
                <div className="flex-1" />
                {controls}
                {onRemove && (
                    <button type="button" onClick={onRemove} className="ml-1 rounded p-1 text-zinc-400 hover:text-[#FD775E]" aria-label="Remove block">
                        <Trash2 size={15} />
                    </button>
                )}
            </div>
            {children}
        </div>
    );
}

function SubFieldRow({ label, exclude, onExclude, onRemove, children }: { label: string; exclude?: boolean; onExclude?: (v: boolean) => void; onRemove: () => void; children: React.ReactNode }) {
    return (
        <div className="grid grid-cols-[84px_1fr_auto] items-start gap-3 border-t border-zinc-100 py-2.5 first:border-t-0">
            <span className="pt-1.5 text-[11px] font-medium uppercase tracking-wide text-zinc-500">{label}</span>
            <div className="min-w-0">{children}</div>
            <div className="flex items-center gap-1 pt-1">
                {onExclude && <ExcludeBtn on={!!exclude} onChange={onExclude} />}
                <button type="button" onClick={onRemove} className="rounded p-0.5 text-zinc-300 hover:text-[#FD775E]" aria-label="Remove field">
                    <X size={14} />
                </button>
            </div>
        </div>
    );
}

function AddFieldRow({ fields, present, onAdd }: { fields: string[]; present: Set<string>; onAdd: (f: string) => void }) {
    const avail = fields.filter((f) => !present.has(f));
    if (!avail.length) return null;
    return (
        <div className="mt-3 flex flex-wrap items-center gap-1.5 border-t border-dashed border-zinc-200 pt-3">
            <span className="mr-1 text-[10px] font-semibold uppercase tracking-wide text-zinc-400">Add</span>
            {avail.map((f) => (
                <button
                    key={f}
                    type="button"
                    onClick={() => onAdd(f)}
                    className="inline-flex items-center gap-1 rounded-full border border-dashed border-zinc-300 px-2.5 py-1 text-[11.5px] text-zinc-500 transition-colors hover:border-[#FD775E] hover:text-[#FD775E]"
                >
                    <Plus size={12} /> {fieldLabel(f)}
                </button>
            ))}
        </div>
    );
}

// ── block cards ──
function WorkExperienceCard({ block, onChange, onRemove }: { block: Extract<Block, { kind: 'experience' }>; onChange: (b: Block) => void; onRemove: () => void }) {
    const setRow = (i: number, leaf: (typeof block.rows)[number]) => onChange({ ...block, rows: block.rows.map((r, x) => (x === i ? leaf : r)) });
    const rmRow = (i: number) => onChange({ ...block, rows: block.rows.filter((_, x) => x !== i) });
    const present = new Set(block.rows.map((r) => r.field));
    // the WHEN pill BULK-sets `when` on every when-eligible row (the leaves' own `when` is the source of truth on compile).
    const setWhen = (w: WhenState) => onChange({ ...block, when: w, rows: block.rows.map((r) => (EXPERIENCE_WHEN.has(r.field) ? { ...r, when: w === 'any' ? undefined : w } : r)) });
    const addRow = (f: string) => onChange({ ...block, rows: [...block.rows, { ...newLeaf(f), ...(block.when !== 'any' && EXPERIENCE_WHEN.has(f) ? { when: block.when } : {}) }] });
    const controls = (
        <>
            <Seg tone="coral" value={block.when} options={[{ value: 'current', label: 'Current job' }, { value: 'past', label: 'A past job' }, { value: 'any', label: 'Any job' }]} onChange={(w) => setWhen(w as WhenState)} />
            <Seg value={block.matchAll ? 'all' : 'any'} options={[{ value: 'all', label: 'Match all' }, { value: 'any', label: 'Match any' }]} onChange={(v) => onChange({ ...block, matchAll: v === 'all' })} />
            <ExcludeBtn on={block.exclude} onChange={(v) => onChange({ ...block, exclude: v })} />
        </>
    );
    return (
        <BlockCard title="Work experience" controls={controls} onRemove={onRemove}>
            {block.rows.map((r, i) => (
                <SubFieldRow key={r.field} label={fieldLabel(r.field)} exclude={r.negate} onExclude={(v) => setRow(i, { ...r, negate: v || undefined })} onRemove={() => rmRow(i)}>
                    <LeafValueEditor f={r} onChange={(leaf) => setRow(i, leaf)} />
                </SubFieldRow>
            ))}
            <AddFieldRow fields={EXPERIENCE_ROW_FIELDS} present={present} onAdd={addRow} />
        </BlockCard>
    );
}

function EducationCard({ block, onChange, onRemove }: { block: Extract<Block, { kind: 'education' }>; onChange: (b: Block) => void; onRemove: () => void }) {
    const setRow = (i: number, leaf: (typeof block.rows)[number]) => onChange({ ...block, rows: block.rows.map((r, x) => (x === i ? leaf : r)) });
    const rmRow = (i: number) => onChange({ ...block, rows: block.rows.filter((_, x) => x !== i) });
    const present = new Set(block.rows.map((r) => r.field));
    const controls = (
        <>
            <Seg value={block.matchAll ? 'all' : 'any'} options={[{ value: 'all', label: 'Match all' }, { value: 'any', label: 'Match any' }]} onChange={(v) => onChange({ ...block, matchAll: v === 'all' })} />
            <ExcludeBtn on={block.exclude} onChange={(v) => onChange({ ...block, exclude: v })} />
        </>
    );
    return (
        <BlockCard title="Education" controls={controls} onRemove={onRemove}>
            {block.rows.map((r, i) => (
                <SubFieldRow key={r.field} label={fieldLabel(r.field)} exclude={r.negate} onExclude={(v) => setRow(i, { ...r, negate: v || undefined })} onRemove={() => rmRow(i)}>
                    <LeafValueEditor f={r} onChange={(leaf) => setRow(i, leaf)} />
                </SubFieldRow>
            ))}
            <AddFieldRow fields={EDUCATION_ROW_FIELDS} present={present} onAdd={(f) => onChange({ ...block, rows: [...block.rows, newLeaf(f)] })} />
        </BlockCard>
    );
}

function SkillsCard({ block, onChange, onRemove }: { block: Extract<Block, { kind: 'skills' }>; onChange: (b: Block) => void; onRemove: () => void }) {
    const controls = (
        <>
            <Seg value={block.matchType} options={[{ value: 'all', label: 'All (AND)' }, { value: 'any', label: 'Any (OR)' }]} onChange={(v) => onChange({ ...block, matchType: v as 'all' | 'any' })} />
            <ExcludeBtn on={block.negate} onChange={(v) => onChange({ ...block, negate: v })} />
        </>
    );
    return (
        <BlockCard title="Skills" controls={controls} onRemove={onRemove}>
            <AutocompleteInput
                placeholder="add skill…"
                values={block.values.map((v) => ({ label: v }))}
                onAdd={(t) => onChange({ ...block, values: [...block.values, t] })}
                onRemove={(i) => onChange({ ...block, values: block.values.filter((_, x) => x !== i) })}
            />
            <Similarity value={block.similarity} onChange={(s) => onChange({ ...block, similarity: s })} />
            <div className="mt-1 text-[11px] italic text-zinc-400">
                {block.matchType === 'all' ? 'must have every listed skill' : 'has any of these skills'} · similarity widens to adjacent skills
            </div>
        </BlockCard>
    );
}

function LanguagesCard({ block, onChange, onRemove }: { block: Extract<Block, { kind: 'languages' }>; onChange: (b: Block) => void; onRemove: () => void }) {
    const setLang = (i: number, patch: Partial<(typeof block.langs)[number]>) => onChange({ ...block, langs: block.langs.map((l, x) => (x === i ? { ...l, ...patch } : l)) });
    const rmLang = (i: number) => onChange({ ...block, langs: block.langs.filter((_, x) => x !== i) });
    return (
        <BlockCard title="Languages" onRemove={onRemove}>
            {/* one row per language, each with its OWN min-proficiency floor */}
            {block.langs.map((l, i) => (
                <SubFieldRow key={`${l.language}-${i}`} label="Speaks" onRemove={() => rmLang(i)}>
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="inline-flex items-center rounded-md bg-zinc-100 px-2 py-1 text-[12.5px] text-[#0D1830]">{l.language}</span>
                        <FilterSelect minWidth={160} value={l.proficiency || 'any'} options={PROFICIENCY_OPTS} onChange={(v) => setLang(i, { proficiency: v === 'any' ? undefined : (v as string) })} />
                    </div>
                </SubFieldRow>
            ))}
            <div className="mt-2">
                <AutocompleteInput
                    placeholder="add language…"
                    values={[]}
                    onAdd={(t) => onChange({ ...block, langs: [...block.langs, { language: t }] })}
                    onRemove={() => {}}
                />
            </div>
        </BlockCard>
    );
}

function FieldCard({ block, onChange, onRemove }: { block: Extract<Block, { kind: 'field' }>; onChange: (b: Block) => void; onRemove: () => void }) {
    const f = block.leaf;
    return (
        <BlockCard title={fieldLabel(f.field)} controls={<ExcludeBtn on={!!f.negate} onChange={(v) => onChange({ ...block, leaf: { ...f, negate: v || undefined } })} />} onRemove={onRemove}>
            <LeafValueEditor f={f} onChange={(leaf) => onChange({ ...block, leaf })} />
        </BlockCard>
    );
}

function RawGroupCard({ block, onChange, onRemove }: { block: Extract<Block, { kind: 'raw' }>; onChange: (b: Block) => void; onRemove: () => void }) {
    const g = block.group;
    return (
        <BlockCard title="Advanced group" controls={<span className="text-[11px] text-zinc-400">{g.combinator === 'or' ? 'Any (OR)' : 'All (AND)'}{g.negate ? ' · NOT' : ''}</span>} onRemove={onRemove}>
            <TreeFilters tree={g} onChange={(next) => onChange({ ...block, group: next })} />
        </BlockCard>
    );
}

function AddChip({ label, onClick }: { label: string; onClick: () => void }) {
    return (
        <button type="button" onClick={onClick} className="inline-flex items-center gap-1 rounded-xl border border-dashed border-zinc-300 px-3 py-1.5 text-[13px] text-zinc-600 transition-colors hover:border-[#FD775E] hover:text-[#0D1830]">
            <Plus size={14} /> {label}
        </button>
    );
}

// ── root editor ──
export function BlockEditor({ tree, onChange }: { tree: Group; onChange: (t: Group) => void }) {
    const [seed] = useState(() => treeToBlocks(tree));
    const [blocks, setBlocks] = useState<Block[]>(seed.blocks);
    const [combinator, setCombinator] = useState<'and' | 'or'>(seed.rootCombinator);
    const [rootNegate, setRootNegate] = useState(seed.rootNegate);

    const commit = (nextBlocks: Block[], nextComb: 'and' | 'or' = combinator, nextNeg: boolean = rootNegate) => {
        setBlocks(nextBlocks);
        setCombinator(nextComb);
        setRootNegate(nextNeg);
        onChange(blocksToTree(nextBlocks, nextComb, nextNeg));
    };
    const setBlock = (id: string, b: Block) => commit(blocks.map((x) => (x.id === id ? b : x)));
    const rmBlock = (id: string) => commit(blocks.filter((x) => x.id !== id));
    const addBlock = (b: Block) => commit([...blocks, b]);

    const render = (b: Block) => {
        const onChg = (nb: Block) => setBlock(b.id, nb);
        const onRm = () => rmBlock(b.id);
        switch (b.kind) {
            case 'experience': return <WorkExperienceCard key={b.id} block={b} onChange={onChg} onRemove={onRm} />;
            case 'education': return <EducationCard key={b.id} block={b} onChange={onChg} onRemove={onRm} />;
            case 'skills': return <SkillsCard key={b.id} block={b} onChange={onChg} onRemove={onRm} />;
            case 'languages': return <LanguagesCard key={b.id} block={b} onChange={onChg} onRemove={onRm} />;
            case 'field': return <FieldCard key={b.id} block={b} onChange={onChg} onRemove={onRm} />;
            case 'raw': return <RawGroupCard key={b.id} block={b} onChange={onChg} onRemove={onRm} />;
        }
    };

    return (
        <div>
            {/* match header */}
            <div className="mb-4 flex items-center gap-3">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-zinc-400">Match</span>
                <Seg value={combinator} options={[{ value: 'and', label: 'All (AND)' }, { value: 'or', label: 'Any (OR)' }]} onChange={(v) => commit(blocks, v as 'and' | 'or')} />
                <ExcludeBtn on={rootNegate} onChange={(n) => commit(blocks, combinator, n)} />
                {seed.dropped > 0 && <span className="text-[11px] text-zinc-400">· {seed.dropped} unsupported filter{seed.dropped === 1 ? '' : 's'} ignored</span>}
            </div>

            {blocks.length === 0 && <div className="mb-3 rounded-xl border border-dashed border-zinc-200 bg-white/50 px-4 py-6 text-center text-[13px] text-zinc-400">No filters yet — add a block below.</div>}
            {blocks.map(render)}

            {/* add-block menu */}
            <div className="mt-2 flex flex-wrap items-center gap-2 border-t border-dashed border-zinc-200 pt-4">
                <span className="mr-1 text-[11px] font-semibold uppercase tracking-wide text-zinc-400">Add block</span>
                <AddChip label="Work experience" onClick={() => addBlock(newExperienceBlock())} />
                <AddChip label="Education" onClick={() => addBlock(newEducationBlock())} />
                <AddChip label="Skills" onClick={() => addBlock(newSkillsBlock())} />
                <AddChip label="Languages" onClick={() => addBlock(newLanguagesBlock())} />
                <div className="w-[190px]">
                    <FilterSelect
                        value=""
                        placeholder="+ a candidate field…"
                        options={[{ value: '', label: '+ a candidate field…' }, ...PERSON_FIELD_BLOCKS.map((f) => ({ value: f, label: fieldLabel(f) }))]}
                        onChange={(v) => { if (v) addBlock(newFieldBlock(v as string)); }}
                    />
                </div>
            </div>
        </div>
    );
}
